import { observable, when } from '@legendapp/state';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';

import translate from '../../utils/translate';
import { COLOR_PREFIX, INTERIOR_PREFIX, RIM_PREFIX } from './nba/utils/get-buildable-config';

import {
	AbTestFeatures,
	EVENTS_WITH_AB_TEST_FEATURES,
	FeatureAppVariantEnum,
	ProductView,
	TrackingConfiguration,
	TrackingEventName,
	TrackingFileInfo,
	TrackingLinkInfo,
	TrackingNBABInfo,
	ZoomActionTypes
} from './tracking/types';

import { degreeImageStore, interiorStore, rootStore } from '../index';
import { featureAppStore } from './feature-app-store';

interface Error {
	code?: string;
	message?: string;
}

interface TrackOptions {
	contentId?: ZoomActionTypes | string;
	content?: TrackingNBABInfo;
	file?: TrackingFileInfo;
	link?: TrackingLinkInfo;
	sectionName?: ProductView;
	resolution?: string;
	degree?: number | null;
	error?: Error;
}

interface TrackingTimers {
	[key: string]: number;
}

type TrackingTenant = 'private' | 'commercial';

interface TrackingStore {
	timers: TrackingTimers;

	track: (eventName: TrackingEventName, options?: TrackOptions) => void;
	setTrackingTimer: (timer: string, eventName: TrackingEventName, options?: TrackOptions) => void;
	cancelTrackingTimer: (timer: string) => void;
}

function getCurrentTrackingConfiguration(): TrackingConfiguration {
	const [_, currentModelValue] = rootStore.currentModel.get();
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [currentInteriorKey] = interiorStore.currentInterior.get();

	const [colorCode, rimCode] = currentVariantKey.split('-');
	const degree = degreeImageStore.currentDegree.get();

	const currentColor = currentModelValue.thumbnailColor?.[colorCode!] || { name: '' };
	const currentRim = currentModelValue.thumbnailRim?.[rimCode!] || { name: '' };
	const currentInterior = currentModelValue.thumbnailInterior?.[currentInteriorKey] || { name: '' };
	const { carlineName, salesgroupName, equipmentLine } = currentModelValue.trimLayerDetails?.model || {};

	const prefixedColorCode = `${COLOR_PREFIX}-${colorCode}`;
	const prefixedRimCode = `${RIM_PREFIX}-${rimCode}`;
	const prefixedInteriorCode = `${INTERIOR_PREFIX}-${currentInteriorKey}`;

	const transformPrNumber = (key: string) => key.replace('_', ' ');

	return {
		ExteriorColorName: currentColor.name || translate(`colors.${prefixedColorCode}`) || undefined,
		ExteriorColorCode: transformPrNumber(prefixedColorCode),
		InteriorColorName: currentInterior.name,
		InteriorColorCode: transformPrNumber(prefixedInteriorCode),
		VehicleAngle: [(degree || 0).toString()],
		WheelsBasicName: currentRim.name || translate(`rims.${prefixedRimCode}`) || undefined,
		WheelsBasicId: transformPrNumber(prefixedRimCode),
		CarlineId: currentModelValue.vehicleConfiguration?.carlineCode || '',
		CarlineName: currentModelValue.vehicleConfiguration?.familyName || carlineName,
		SalesgroupId: currentModelValue.vehicleConfiguration?.salesgroupCode || '',
		SalesgroupName: currentModelValue.vehicleConfiguration?.trimName || salesgroupName,
		EquipmentlineName: currentModelValue.vehicleConfiguration?.modelName || equipmentLine || ''
	};
}

function getCurrentTrackingEnvironment() {
	const appVersion = process.env.FEATURE_APP_VERSION || '';
	const featureAppId = featureAppStore.featureAppId.get() || '';
	return {
		featureAppId,
		featureAppIdAppVersion: appVersion,
		featureAppVariant: FeatureAppVariantEnum.imageBased
	};
}

export const trackingStore = observable<TrackingStore>({
	timers: {},
	track: (eventName: TrackingEventName, options?: TrackOptions): void => {
		const featureServices = featureAppStore.featureServices.get()!;
		const trackingService = featureServices['tracking'] as TrackingManagerV1;
		const isInteriorLoaded = interiorStore.isInteriorLoaded.get();

		const [_, currentModelValue] = rootStore.currentModel.get();
		const tenant = currentModelValue.vehicleConfiguration?.tenant;

		const eventInfo: {
			eventAction: TrackingEventName;
			contentId: TrackOptions['contentId'];
			brand: TrackingTenant;
			sectionName: ProductView;
			abTestFeatures?: AbTestFeatures;
		} = {
			eventAction: eventName,
			contentId: options?.contentId,
			brand: tenant?.slice(6).toLowerCase() as TrackingTenant,
			sectionName: isInteriorLoaded ? ProductView.INTERIOR_VIEW : ProductView.EXTERNAL_VIEW
		};

		if (EVENTS_WITH_AB_TEST_FEATURES.has(eventName)) {
			eventInfo.abTestFeatures = featureAppStore.abTestFeatures.get();
		}

		const trackingData = {
			configuration: getCurrentTrackingConfiguration(),
			environment: getCurrentTrackingEnvironment(),
			eventInfo
		};

		trackingService.track(eventName, trackingData);
	},

	setTrackingTimer: (timer: string, eventName: TrackingEventName, options?: TrackOptions): void => {
		const timers = trackingStore.timers.get();

		if (timers[timer]) {
			trackingStore.cancelTrackingTimer(timer);
		}

		const timerId = window.setTimeout(() => {
			trackingStore.track(eventName, { ...options });
			const currentTimers = trackingStore.timers.get();
			const { [timer]: _, ...remainingTimers } = currentTimers;
			trackingStore.timers.set(remainingTimers);
		}, 300);

		trackingStore.timers.set({ ...timers, [timer]: timerId });
	},

	cancelTrackingTimer: (timer: string): void => {
		const timers = trackingStore.timers.get();
		const timerId = timers[timer];

		if (!timerId) return;

		clearTimeout(timerId);

		// Remove timer reference
		const { [timer]: _, ...remainingTimers } = timers;
		trackingStore.timers.set(remainingTimers);
	}
});

when(
	() => featureAppStore.featureServices.get() && featureAppStore.featureAppId.get(),
	() => {
		return () => {
			const timers = trackingStore.timers.get();
			Object.values(timers).forEach(timerId => {
				clearTimeout(timerId);
			});
			trackingStore.timers.set({});
		};
	}
);
